import type * as CSS from 'csstype'
import { system, Config } from '@styled-system/core'
import { getNegativeSpace } from '../utils/getNegativeSpace'
import { ResponsiveValue, TLength } from '../utils/types'

export interface PositionProps {
  /**
   * The bottom CSS property participates in specifying the vertical position of a
   * positioned element. It has no effect on non-positioned elements.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/top)
   */
  bottom?: ResponsiveValue<CSS.BottomProperty<TLength>>
  /**
   * The left CSS property participates in specifying the horizontal position
   * of a positioned element. It has no effect on non-positioned elements.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/left)
   */
  left?: ResponsiveValue<CSS.LeftProperty<TLength>>
  /**
   * The position CSS property specifies how an element is positioned in a document.
   * The top, right, bottom, and left properties determine the final location of positioned elements.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/position)
   */
  position?: ResponsiveValue<CSS.PositionProperty>
  /**
   * The right CSS property participates in specifying the horizontal position of a
   * positioned element. It has no effect on non-positioned elements.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/right)
   */
  right?: ResponsiveValue<CSS.RightProperty<TLength>>
  /**
   * The top CSS property participates in specifying the vertical position of a
   * positioned element. It has no effect on non-positioned elements.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/top)
   */
  top?: ResponsiveValue<CSS.TopProperty<TLength>>
  /**
   * The z-index CSS property sets the z-order of a positioned element and its descendants or
   * flex items. Overlapping elements with a larger z-index cover those with a smaller one.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/z-index)
   */
  zIndex?: ResponsiveValue<CSS.ZIndexProperty>
}

export const config: Config = {
  bottom: {
    property: 'bottom',
    scale: 'space',
    transform: getNegativeSpace
  },
  left: {
    property: 'left',
    scale: 'space',
    transform: getNegativeSpace
  },
  position: true,
  right: {
    property: 'right',
    scale: 'space',
    transform: getNegativeSpace
  },
  top: {
    property: 'top',
    scale: 'space',
    transform: getNegativeSpace
  },
  zIndex: {
    property: 'zIndex',
    scale: 'zIndices'
  }
}

export const positionProps = system(config)
